// src/components/GatheringsList.js
import React, { useState, useEffect } from "react";
import { standardUserApi } from "../services/standardUserApi";
import { getPrettyDateTime } from "../utils";
import mixpanel from "mixpanel-browser";

// Gathering images function - uses theme images from database only
const getGatheringImage = (gatheringType, themeId, themes = []) => {
    // Get image from theme database
    if (themeId && themes.length > 0) {
        const theme = themes.find(t => t.id === themeId);
        if (theme && (theme.hero_image_url || theme.image_url)) {
            return theme.hero_image_url || theme.image_url;
        }
    }

    // No hardcoded fallbacks - return null if no image is set
    // This encourages admins to set images for all themes
    return null;
};

const GatheringsList = ({
    mode = "display", // "display" or "booking"
    onGatheringClick = null,
    maxItems = mode === "booking" ? 50 : 12, // More items for booking mode
    selectedSlotIds = [],
    onSlotSelection = null,
    onFiltersChange = null,
    initialThemeId = null, // Pre-selected theme ID from sessionStorage
    initialThemeName = '' // Pre-selected theme name for display
}) => {
    // Data state
    const [allSlots, setAllSlots] = useState([]);
    const [userBookedSlots, setUserBookedSlots] = useState([]);
    const [bookedSlots, setBookedSlots] = useState([]);
    const [themes, setThemes] = useState([]);
    const [userLocation, setUserLocation] = useState(null);
    const [unavailabilityData, setUnavailabilityData] = useState({ general: [], theme_specific: {} });
    const [cityLocations, setCityLocations] = useState([]);   // active locations in user's city
    const [selectedLocationId, setSelectedLocationId] = useState(null);
    const [loading, setLoading] = useState(true);

    // Internal filter state - centralized management
    // If initialThemeId is provided, we use it as the selected theme and don't show "all types"
    const [gatheringType, setGatheringType] = useState(initialThemeId ? 'themed' : 'all');
    const [selectedTheme, setSelectedTheme] = useState(initialThemeId ? String(initialThemeId) : '');

    const [sourceThemeId, setSourceThemeId] = useState(null);

    useEffect(() => {
        const stored = localStorage.getItem('sourceThemeId');
        setSourceThemeId(stored ? parseInt(stored, 10) : null);
    }, []);

    // Sync internal state when initialThemeId prop changes
    useEffect(() => {
        if (initialThemeId) {
            setGatheringType('themed');
            setSelectedTheme(String(initialThemeId));
        } else {
            setGatheringType('all');
            setSelectedTheme('');
        }
    }, [initialThemeId]);


    // State for handling date conflict messaging
    const [dateConflictMessage, setDateConflictMessage] = useState(null);

    // Track which gathering type was selected for each slot
    const [selectedGatheringTypes, setSelectedGatheringTypes] = useState(new Map());

    // Theme ID constants for readability
    const technology_theme_id = 1;
    const finance_theme_id = 3;
    const marketing_theme_id = 4;

    // Get General Social theme ID from database
    const getGeneralSocialThemeId = () => {
        const socialTheme = themes.find(theme => theme.theme_name === 'General Social');
        return socialTheme ? socialTheme.id : null;
    };

    const general_social_theme_id = getGeneralSocialThemeId();

    // Helper to get display label for theme type
    const getThemeTypeDisplayLabel = (themeType) => {
        const labels = {
            'casual': 'Social Gatherings',
            'interest_based': 'Interest Themed',
            'professional': 'Professional',
            'country': 'Countries'
        };
        return labels[themeType] || themeType;
    };

    // Build theme schedule mapping from database theme configuration
    const buildThemeScheduleMapping = () => {
        const mapping = {};
        themes.forEach(theme => {
            if (theme.day_of_week && theme.week_of_month) {
                mapping[theme.id] = {
                    day_of_week: theme.day_of_week,
                    week_of_month: theme.week_of_month
                };
            }
        });
        return mapping;
    };

    const THEME_SCHEDULE_MAPPING = buildThemeScheduleMapping();

    // Booking conflict logic from Slots.js
    const hasBookingConflictOnDate = (slotDate) => {
        if (!slotDate || isNaN(slotDate.getTime())) {
            return false;
        }

        const slotDateOnly = slotDate.toISOString().split('T')[0];
        return userBookedSlots.some(bookedSlot => {
            if (!bookedSlot.date_time) {
                return false;
            }

            const bookedDate = new Date(bookedSlot.date_time);
            if (isNaN(bookedDate.getTime())) {
                return false;
            }

            const bookedDateOnly = bookedDate.toISOString().split('T')[0];
            return bookedDateOnly === slotDateOnly;
        });
    };

    const getExistingBookingType = (slotDate) => {
        if (!slotDate || isNaN(slotDate.getTime())) {
            return null;
        }

        const slotDateOnly = slotDate.toISOString().split('T')[0];
        const existingBooking = userBookedSlots.find(bookedSlot => {
            if (!bookedSlot.date_time) {
                return false;
            }

            const bookedDate = new Date(bookedSlot.date_time);
            if (isNaN(bookedDate.getTime())) {
                return false;
            }

            const bookedDateOnly = bookedDate.toISOString().split('T')[0];
            return bookedDateOnly === slotDateOnly;
        });

        if (!existingBooking) {
            return null;
        }

        // Check if it's a general social gathering (casual) or professional/interest themed
        return existingBooking.theme_id === general_social_theme_id ? 'casual' : 'professional';
    };

    // City restrictions logic - now database-driven via API
    const getCityUnavailableDates = () => {
        // Returns general (non-theme-specific) unavailable dates from database
        return unavailabilityData.general || [];
    };

    const getThemeCityDateRestrictions = (themeId, currentGatheringType) => {
        // Returns theme-specific unavailable dates from database
        if (!themeId) {
            return [];
        }

        return unavailabilityData.theme_specific[parseInt(themeId)] || [];
    };

    // Urgency messages logic from Slots.js
    const urgencyMessages = [
        " (🔥 Almost full!)",
        " (⚡ Only a few spots left!)",
        " (🎯 Nearly full!)",
        " (🏃 Filling up fast!)",
        " (🚀 High demand!)",
        " (✨ Popular choice!)"
    ];

    const getUrgencyMessage = (slotId, currentDate) => {
        const seed = slotId + currentDate.toDateString();
        const hash = seed.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0);
        const index = Math.abs(hash) % urgencyMessages.length;
        return urgencyMessages[index];
    };

    // Helper function to get theme-specific messaging
    const getThemeSpecificMessage = () => {
        if (gatheringType !== 'professional' || !selectedTheme) {
            return null;
        }

        const selectedThemeObj = themes.find(theme => theme.id == selectedTheme);
        if (!selectedThemeObj) {
            return null;
        }

        // Handle weekly recurring themes
        if (selectedThemeObj.weekly && selectedThemeObj.day_of_week) {
            return `${selectedThemeObj.theme_name} gatherings happen every ${selectedThemeObj.day_of_week}.`;
        }

        // Handle monthly scheduled themes
        const schedule = THEME_SCHEDULE_MAPPING[parseInt(selectedTheme)];
        if (schedule) {
            const ordinals = ['', 'first', 'second', 'third', 'fourth', 'fifth'];
            return `${selectedThemeObj.theme_name} gatherings happen on the ${ordinals[schedule.week_of_month]} ${schedule.day_of_week} of each month.`;
        }

        return null;
    };

    // Load data
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // Load themes
                const lookupData = await standardUserApi.getLookupData();
                setThemes(lookupData.themes || []);

                if (mode === "display") {
                    // For display mode (Homepage), use public endpoint
                    const slots = await standardUserApi.getFutureSlotsPublic();
                    setAllSlots(slots);
                } else {
                    // For booking mode (Slots page), use authenticated endpoint
                    const token = localStorage.getItem("memberstackToken");
                    if (!token) {
                        setLoading(false);
                        return;
                    }

                    // Fetch user profile and locations first so we can include location_id in slots request
                    let userLocationId = null;
                    try {
                        const [user, allLocs] = await Promise.all([
                            standardUserApi.getUserProfile(token),
                            fetch('/api/open/locations-rows').then(r => r.json())
                        ]);
                        const userCity = user.location?.city;
                        userLocationId = user.location?.id || null;
                        const sameCityLocs = userCity
                            ? allLocs.filter(l => l.city === userCity && l.is_active !== false)
                            : [];
                        setCityLocations(sameCityLocs);
                        setSelectedLocationId(userLocationId);
                        setUserLocation({
                            id: userLocationId,
                            area: user.location?.area,
                            city: user.location?.city,
                            country: user.location?.country
                        });
                    } catch (error) {
                        console.error("Failed to fetch user location:", error);
                    }

                    const slots = await standardUserApi.getFutureSlots(token, userLocationId);
                    console.log("DEBUG: Total slots loaded:", slots.length);
                    setAllSlots(slots);

                    // Load user booking data for conflict checking
                    const userSlots = await standardUserApi.getUserSlots(token);
                    setUserBookedSlots(userSlots);
                    const booked = userSlots.map((us) => us.slot_id);
                    setBookedSlots(booked);
                }
            } catch (error) {
                console.error("Failed to load data:", error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [mode]);

    // Fetch gathering unavailability from database when user location is known
    useEffect(() => {
        const fetchUnavailability = async () => {
            try {
                const locationId = userLocation?.id;
                const url = locationId
                    ? `/api/open/gathering-unavailability?location_id=${locationId}`
                    : '/api/open/gathering-unavailability';

                const response = await fetch(url);
                if (response.ok) {
                    const data = await response.json();
                    setUnavailabilityData(data);
                } else {
                    console.error("Failed to fetch gathering unavailability");
                }
            } catch (error) {
                console.error("Error fetching gathering unavailability:", error);
            }
        };

        // Fetch unavailability data when location is available or on initial mount
        fetchUnavailability();
    }, [userLocation?.id]);

    // Sync selectedGatheringTypes with parent's selectedSlotIds
    useEffect(() => {
        setSelectedGatheringTypes(prev => {
            const newMap = new Map();
            // Only keep entries for slots that are still selected
            prev.forEach((gatheringType, slotId) => {
                if (selectedSlotIds.includes(slotId)) {
                    newMap.set(slotId, gatheringType);
                }
            });
            return newMap;
        });
    }, [selectedSlotIds]);

    // Notify parent component when filters change (for Slots.js integration)
    useEffect(() => {
        if (onFiltersChange) {
            onFiltersChange(gatheringType, selectedTheme);
        }
    }, [gatheringType, selectedTheme, onFiltersChange]);

    // Expand slots into all possible gatherings
    // NEW: Slots now come from API with theme_id already included - no need to compute!
    const expandSlotsToGatherings = () => {
        const gatherings = [];

        allSlots.forEach(slot => {
            const slotDate = new Date(slot.date_and_time);
            const isUpcoming = slotDate > new Date();

            if (!isUpcoming) return;

            // NEW: Backend now tells us the theme for this slot
            const slotThemeId = slot.theme_id;
            const canBeProfessional = slotThemeId !== null && slotThemeId !== general_social_theme_id;

            // Apply time range filtering
            const now = new Date();
            const casualCutoff = new Date();
            casualCutoff.setDate(now.getDate() + (4 * 7));
            const professionalCutoff = casualCutoff;

            // Always add casual option (within 4 weeks)
            if (slotDate <= casualCutoff) {
                gatherings.push({
                    id: `${slot.id}-casual`,
                    slotId: slot.id,
                    slot: slot,
                    gatheringType: 'casual',
                    themeId: general_social_theme_id,
                    date_and_time: slot.date_and_time
                });
            }

            // Add professional option if date supports it (within 4 weeks)
            if (canBeProfessional && slotDate <= professionalCutoff) {
                gatherings.push({
                    id: `${slot.id}-professional-${slotThemeId}`,
                    slotId: slot.id,
                    slot: slot,
                    gatheringType: 'professional',
                    themeId: slotThemeId,
                    date_and_time: slot.date_and_time
                });
            }
        });

        return gatherings;
    };

    // Helper function to apply source theme filtering
    const applySourceThemeFilter = (gatherings) => {
        if (!sourceThemeId) {
            // No source theme - show first date of each theme
            const themeFirstDates = {};
            gatherings.forEach(gathering => {
                const themeId = gathering.themeId;
                if (!themeFirstDates[themeId] ||
                    new Date(gathering.date_and_time) < new Date(themeFirstDates[themeId].date_and_time)) {
                    themeFirstDates[themeId] = gathering;
                }
            });
            return Object.values(themeFirstDates);
        }

        // Source theme provided - filter to that theme only
        const sourceTheme = themes.find(t => t.id === sourceThemeId);
        const isWeekly = sourceTheme?.weekly || false;
        const maxGatherings = isWeekly ? 2 : 1;

        const themeGatherings = gatherings.filter(g => g.themeId === sourceThemeId);

        // Sort by date and take first N
        themeGatherings.sort((a, b) =>
            new Date(a.date_and_time) - new Date(b.date_and_time)
        );

        return themeGatherings.slice(0, maxGatherings);
    };

    // Complex filtering logic from Slots.js - now works on gatherings
    const getFilteredGatherings = () => {
        const allGatherings = expandSlotsToGatherings();

        let filtered = allGatherings.filter((gathering) => {
            // Apply filter-based logic
            if (gatheringType === 'all') {
                // For "All Types": show both casual and professional gatherings
                // (but will still filter unavailable below)
            } else if (gatheringType === 'casual') {
                // For "Casual": only show casual gatherings
                if (gathering.gatheringType !== 'casual') return false;
            } else if (gatheringType === 'professional') {
                // For "Professional": only show professional gatherings
                if (gathering.gatheringType !== 'professional') return false;

                // If specific theme selected, only show that theme
                if (selectedTheme && gathering.themeId !== parseInt(selectedTheme)) {
                    return false;
                }
            } else if (gatheringType === 'themed' && selectedTheme) {
                // For pre-selected theme: only show gatherings matching the specific theme
                if (gathering.themeId !== parseInt(selectedTheme)) {
                    return false;
                }
            }

            // Check if gathering is unavailable
            const slot = gathering.slot;
            const slotDate = new Date(gathering.date_and_time);
            const now = new Date();
            const diffMinutes = (slotDate - now) / (1000 * 60);
            const slotDateString = slotDate.toISOString().split("T")[0];

            const unavailableDates = [
                "2025-09-30",
                "2025-10-07",
                ...getCityUnavailableDates(),
                ...getThemeCityDateRestrictions(gathering.themeId, gathering.gatheringType)
            ];

            const isUnavailable = diffMinutes < 30 || unavailableDates.includes(slotDateString);

            // Check if user has booked this specific gathering
            const isBooked = userBookedSlots.some(bookedSlot => {
                if (bookedSlot.slot_id !== slot.id) return false;

                const bookedIsProfessional = bookedSlot.theme_id != null;
                const currentIsProfessional = gathering.gatheringType === 'professional';

                if (bookedIsProfessional !== currentIsProfessional) {
                    return false;
                }

                if (currentIsProfessional && bookedSlot.theme_id !== gathering.themeId) {
                    return false;
                }

                return true;
            });

            // Hide unavailable gatherings UNLESS the user has booked them
            if (isUnavailable && !isBooked) {
                return false;
            }

            return true;
        });

        // Sort gatherings: professional before casual on same date
        filtered.sort((a, b) => {
            const aDate = new Date(a.date_and_time);
            const bDate = new Date(b.date_and_time);

            // First sort by date (earliest first)
            const dateComparison = aDate - bDate;
            if (dateComparison !== 0) {
                return dateComparison;
            }

            // If same date, professional gatherings come first
            if (a.gatheringType === 'professional' && b.gatheringType === 'casual') {
                return -1; // a (professional) comes before b (casual)
            }
            if (a.gatheringType === 'casual' && b.gatheringType === 'professional') {
                return 1; // b (professional) comes before a (casual)
            }

            // If same type or other comparison, maintain current order
            return 0;
        });

        // Apply source theme filtering BEFORE maxItems limit
        if (sourceThemeId !== null) {
            filtered = applySourceThemeFilter(filtered);
        }

        // Apply specific limits for different gathering types (only if no source theme filter)
        if (!sourceThemeId) {
            if (gatheringType === 'casual') {
                // Limit casual gatherings to 3 items max
                filtered = filtered.slice(0, 3);
            } else {
                // For other types, use the general maxItems limit
                filtered = filtered.slice(0, maxItems);
            }
        }

        console.log(`DEBUG: Total gatherings after filtering: ${filtered.length}, maxItems: ${maxItems}, sourceThemeId: ${sourceThemeId}`);
        console.log(`DEBUG: Gathering type filter: ${gatheringType}`);
        if (gatheringType === 'professional') {
            console.log("DEBUG: Filtered professional gatherings:", filtered);
        }

        return filtered;
    };

    // Helper function to check if there's already a selection on the same date
    const hasDateConflict = (newGatheringDate, newGatheringSlotId) => {
        if (!selectedSlotIds.length) return false;

        const newDateOnly = new Date(newGatheringDate).toISOString().split('T')[0];

        // Check if any selected slot is on the same date (excluding the current slot itself)
        return allSlots.some(slot => {
            if (!selectedSlotIds.includes(slot.id)) return false;
            if (slot.id === newGatheringSlotId) return false; // Exclude current slot

            const slotDateOnly = new Date(slot.date_and_time).toISOString().split('T')[0];
            return slotDateOnly === newDateOnly;
        });
    };

    // Helper function to check if user has already booked a gathering on this date
    const hasExistingBookingOnDate = (gatheringDate) => {
        if (!gatheringDate || isNaN(gatheringDate.getTime())) {
            return false;
        }

        const gatheringDateOnly = gatheringDate.toISOString().split('T')[0];
        return userBookedSlots.some(bookedSlot => {
            if (!bookedSlot.date_time) return false;

            const bookedDate = new Date(bookedSlot.date_time);
            if (isNaN(bookedDate.getTime())) return false;

            const bookedDateOnly = bookedDate.toISOString().split('T')[0];
            return bookedDateOnly === gatheringDateOnly;
        });
    };

    const handleGatheringClick = (gathering) => {
        if (mode === "display" && onGatheringClick) {
            onGatheringClick(gathering.slot);
        } else if (mode === "booking" && onSlotSelection) {
            const slotId = gathering.slotId;
            const gatheringType = gathering.gatheringType;

            // Check if this slot is already selected
            const isCurrentlySelected = selectedSlotIds.includes(slotId);

            if (!isCurrentlySelected) {
                // Check for date conflicts before allowing new selections
                if (hasDateConflict(gathering.date_and_time, slotId)) {
                    // Show conflict message on this specific gathering
                    setDateConflictMessage(gathering.id);

                    // Clear the message after 4 seconds
                    setTimeout(() => {
                        setDateConflictMessage(null);
                    }, 4000);

                    return; // Don't proceed with selection
                }
            }

            // Update the selected gathering types map
            setSelectedGatheringTypes(prev => {
                const newMap = new Map(prev);
                const currentType = newMap.get(slotId);

                if (currentType === gatheringType) {
                    // Same type clicked - deselect
                    newMap.delete(slotId);
                } else {
                    // Different type or no selection - select this type
                    newMap.set(slotId, gatheringType);
                }

                return newMap;
            });

            // Call parent's selection handler with gathering data
            const selectedLoc = cityLocations.find(l => l.id === selectedLocationId) || userLocation;
            onSlotSelection({
                slotId: slotId,
                datetime: gathering.date_and_time,  // Pass datetime for backend API
                gatheringType: gatheringType,
                themeId: gathering.themeId,  // Now both casual (social) and professional have themeIds
                locationId: selectedLocationId,
                locationCity: selectedLoc?.city || null,
                locationArea: selectedLoc?.area || null,
            });
        }
    };

    const filteredGatherings = getFilteredGatherings();

    // Loading state
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
                backgroundColor: '#fff',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(139, 95, 191, 0.08)',
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #8B5FBF',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '20px',
                }} />
                <h3 style={{
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    color: '#8B5FBF',
                    margin: '0 0 8px 0',
                }}>
                    Loading gatherings...
                </h3>
                <p style={{
                    fontSize: '0.95rem',
                    color: '#666',
                    margin: 0,
                }}>
                    Finding available gatherings for you
                </p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div>
            <style>{`
                @keyframes fadeInScale {
                    0% {
                        opacity: 0;
                        transform: scale(0.95) translateY(10px);
                    }
                    100% {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }
            `}</style>

            {/* Area selector — only in booking mode when city has multiple areas */}
            {mode === "booking" && cityLocations.length > 1 && (
                <div style={{
                    backgroundColor: '#F5F3FF',
                    border: '2px solid #8B5FBF',
                    borderRadius: '10px',
                    padding: '14px 18px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px',
                }}>
                    <label style={{ fontWeight: '700', color: '#5B21B6', fontSize: '0.95rem', flexShrink: 0 }}>
                        📍 Which area are you booking for?
                    </label>
                    <select
                        value={selectedLocationId || ''}
                        onChange={e => setSelectedLocationId(parseInt(e.target.value))}
                        style={{
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: '1.5px solid #8B5FBF',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            color: '#374151',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                        }}
                    >
                        {cityLocations.map(l => (
                            <option key={l.id} value={l.id}>{l.area}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Gatherings Display */}
            <div style={{
                display: mode === "display" ? "flex" : "flex",
                flexDirection: mode === "display" ? "column" : "row",
                flexWrap: mode === "booking" ? "wrap" : "nowrap",
                gap: "16px",
                maxWidth: mode === "display" ? "600px" : "none",
                margin: mode === "display" ? "0 auto" : "0",
            }}>
                {filteredGatherings.map((gathering) => {
                    const slot = gathering.slot;
                    const slotDate = new Date(gathering.date_and_time);
                    const now = new Date();
                    const diffMinutes = (slotDate - now) / (1000 * 60);
                    const slotDateString = slotDate.toISOString().split("T")[0];

                    // All the availability checking logic - now using database-driven data
                    const unavailableDates = [
                        ...getCityUnavailableDates(),
                        ...getThemeCityDateRestrictions(gathering.themeId, gathering.gatheringType)];

                    const isUnavailable = diffMinutes < 30 || unavailableDates.includes(slotDateString);

                    // FIXED: Check if THIS SPECIFIC gathering (not just slot) is booked
                    const isBooked = userBookedSlots.some(bookedSlot => {
                        if (bookedSlot.slot_id !== slot.id) return false;

                        // Check if gathering types match
                        const bookedIsProfessional = bookedSlot.theme_id !== general_social_theme_id;
                        const currentIsProfessional = gathering.gatheringType === 'professional';

                        if (bookedIsProfessional !== currentIsProfessional) {
                            return false;
                        }

                        // For professional gatherings, also check theme matches
                        if (currentIsProfessional && bookedSlot.theme_id !== gathering.themeId) {
                            return false;
                        }

                        return true;
                    });

                    const hasConflict = hasBookingConflictOnDate(slotDate);
                    const conflictType = hasConflict ? getExistingBookingType(slotDate) : null;

                    // Declare these variables before they're used
                    const closingSoon = diffMinutes > 30 && diffMinutes <= (48 * 60);
                    const likelyNearlyFull = diffMinutes > (2 * 24 * 60) && diffMinutes < (7 * 24 * 60);
                    const urgencyMessage = likelyNearlyFull ? getUrgencyMessage(slot.id, now) : null;

                    // FIXED: Check for existing booking on same date but DIFFERENT gathering type
                    const hasExistingBooking = userBookedSlots.some(bookedSlot => {
                        if (!bookedSlot.date_time) return false;

                        const bookedDate = new Date(bookedSlot.date_time);
                        if (isNaN(bookedDate.getTime())) return false;

                        const bookedDateOnly = bookedDate.toISOString().split('T')[0];
                        const currentDateOnly = slotDate.toISOString().split('T')[0];

                        if (bookedDateOnly !== currentDateOnly) return false;

                        // If dates match, check if it's a DIFFERENT gathering type
                        if (bookedSlot.slot_id === slot.id) {
                            // Same slot - check if different gathering type
                            const bookedIsProfessional = bookedSlot.theme_id !== general_social_theme_id;
                            const currentIsProfessional = gathering.gatheringType === 'professional';

                            if (bookedIsProfessional !== currentIsProfessional) {
                                return true; // Different types on same slot
                            }

                            // Both professional - check if different themes
                            if (currentIsProfessional && bookedSlot.theme_id !== gathering.themeId) {
                                return true; // Different themes
                            }

                            return false; // Same gathering, not a conflict
                        }

                        // Different slot, same date = conflict
                        return true;
                    });

                    const hasTempDateConflict = hasDateConflict(gathering.date_and_time, gathering.slotId);

                    // Determine the state and styling
                    let cardState = "available";
                    let statusMessage = "";
                    let shouldDisable = false;

                    if (mode === "booking") {
                        if (isBooked) {
                            // User has booked this EXACT gathering/slot (same theme)
                            cardState = "going";
                            statusMessage = `You're already signed up for this ${themeName ? `${themeName} ` : ""}gathering — see it on My Gatherings ✓`;
                            shouldDisable = true;
                        } else if (hasExistingBooking) {
                            // User has booked a DIFFERENT gathering on the same date
                            cardState = "conflict";
                            statusMessage = " (You've already signed up for a gathering on this date)";
                            shouldDisable = true;
                        } else if (hasTempDateConflict) {
                            // User has selected another gathering for this date but hasn't confirmed yet
                            cardState = "temp-conflict";
                            statusMessage = " (Same date as gathering already selected)";
                            shouldDisable = true;
                        } else if (isUnavailable) {
                            cardState = "unavailable";
                            statusMessage = " (FULL / unavailable)";
                            shouldDisable = true;
                        } else if (closingSoon) {
                            statusMessage = " (🕒 signups close soon!)";
                        } else if (urgencyMessage) {
                            statusMessage = urgencyMessage;
                        }
                    }

                    const isSlotSelected = selectedSlotIds.includes(slot.id);
                    const selectedTypeForSlot = selectedGatheringTypes.get(slot.id);
                    const isSelected = isSlotSelected && selectedTypeForSlot === gathering.gatheringType;

                    let readableDate = "TBA";
                    try {
                        const dt = new Date(gathering.date_and_time);
                        readableDate = isNaN(dt.getTime()) ? "TBA" : getPrettyDateTime(dt);
                    } catch (error) {
                        // ignore
                    }

                    const displayGatheringType = gathering.gatheringType;
                    const displayThemeId = gathering.themeId;
                    const themeObj = displayThemeId ? themes.find(t => t.id === displayThemeId) : null;
                    const themeName = themeObj ? themeObj.theme_name : null;
                    const themeType = themeObj ? themeObj.theme_type : 'casual';
                    const displayLabel = getThemeTypeDisplayLabel(themeType);

                    if (mode === "display") {
                        // Display mode rendering (Home.js style)
                        return (
                            <div
                                key={gathering.id}
                                style={{
                                    backgroundColor: "#fff",
                                    borderRadius: "15px",
                                    overflow: "hidden",
                                    boxShadow: "0 4px 15px rgba(139, 95, 191, 0.08)",
                                    border: "3px solid transparent",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                }}
                                onClick={() => handleGatheringClick(gathering)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                    e.currentTarget.style.boxShadow = "0 8px 25px rgba(139, 95, 191, 0.2)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 4px 15px rgba(139, 95, 191, 0.08)";
                                }}
                            >
                                {/* Image */}
                                <div style={{
                                    height: "200px",
                                    background: `linear-gradient(45deg, ${displayGatheringType === 'professional' ? '#8B5FBF' : '#2c2e96'}, ${displayGatheringType === 'professional' ? '#6B46C1' : '#8B5FBF'})`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontSize: "3rem",
                                    position: "relative",
                                    overflow: "hidden",
                                }}>
                                    {getGatheringImage(displayGatheringType, displayThemeId, themes) ? (
                                        <img
                                            src={getGatheringImage(displayGatheringType, displayThemeId, themes)}
                                            alt={`${displayGatheringType} gathering`}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                            onError={(e) => {
                                                // Fallback to gradient background with emoji if image fails
                                                e.target.style.display = "none";
                                                e.target.parentElement.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; font-size: 3rem;">${displayGatheringType === 'professional' ? '🤝' : '🍻'}</div>`;
                                            }}
                                        />
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '3rem' }}>
                                            {displayGatheringType === 'professional' ? '🤝' : '🍻'}
                                        </div>
                                    )}
                                    <div style={{
                                        position: "absolute",
                                        top: "10px",
                                        right: "10px",
                                        backgroundColor: displayGatheringType === 'professional' ? "rgba(139, 95, 191, 0.9)" : "rgba(44, 46, 150, 0.9)",
                                        color: "#fff",
                                        padding: "4px 8px",
                                        borderRadius: "12px",
                                        fontSize: "0.8rem",
                                        fontWeight: "600",
                                    }}>
                                        {displayLabel}
                                    </div>
                                </div>

                                {/* Content */}
                                <div style={{ padding: "20px" }}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        marginBottom: "12px",
                                    }}>
                                        <h4 style={{
                                            margin: 0,
                                            fontSize: "1.1rem",
                                            fontWeight: "700",
                                            color: "#333",
                                        }}>
                                            {themeName ? `${themeName}` : `${displayLabel}`}
                                        </h4>
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "8px 12px",
                                            backgroundColor: "rgba(139, 95, 191, 0.1)",
                                            borderRadius: "12px",
                                            fontSize: "0.85rem",
                                            fontWeight: "600",
                                            color: "#8B5FBF",
                                        }}>
                                            Book a spot →
                                        </div>
                                    </div>

                                    <p style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "0.95rem",
                                        color: "#666",
                                        fontWeight: "600",
                                    }}>
                                        📅 {readableDate}
                                    </p>

                                    <p style={{
                                        margin: "0 0 12px 0",
                                        fontSize: "0.9rem",
                                        color: "#666",
                                    }}>
                                        📍 Available in multiple locations
                                    </p>

                                    <p style={{
                                        margin: "0 0 12px 0",
                                        fontSize: "0.9rem",
                                        color: "#666",
                                    }}>
                                        {displayGatheringType === 'professional'
                                            ? 'Friendly networking with people in your industry!'
                                            : 'Join a gathering and meet new people over drinks!'}
                                    </p>

                                </div>
                            </div>
                        );
                    } else {
                        // Helper function to get card styling based on state
                        const getCardStyling = () => {
                            const baseStyle = {
                                backgroundColor: "#fff",
                                borderRadius: "15px",
                                overflow: "hidden",
                                cursor: shouldDisable ? "not-allowed" : "pointer",
                                transition: "all 0.3s ease",
                                position: "relative",
                                minWidth: "280px",
                                maxWidth: "400px",
                                flex: "1 1 300px",
                            };

                            switch (cardState) {
                                case "going":
                                    return {
                                        ...baseStyle,
                                        backgroundColor: "#F0F9FF", // Light blue/green background
                                        boxShadow: "0 4px 15px rgba(34, 197, 94, 0.2)",
                                        border: "3px solid #22C55E",
                                    };
                                case "temp-conflict":
                                    return {
                                        ...baseStyle,
                                        backgroundColor: "#FEF2F2", // Light red background
                                        boxShadow: "0 4px 15px rgba(239, 68, 68, 0.2)",
                                        border: "3px solid #EF4444",
                                        opacity: 0.8,
                                    };
                                case "conflict":
                                case "unavailable":
                                    return {
                                        ...baseStyle,
                                        boxShadow: "0 4px 15px rgba(139, 95, 191, 0.08)",
                                        border: "3px solid #ccc",
                                        opacity: 0.6,
                                    };
                                default:
                                    return {
                                        ...baseStyle,
                                        boxShadow: isSelected
                                            ? "0 8px 25px rgba(139, 95, 191, 0.3)"
                                            : "0 4px 15px rgba(139, 95, 191, 0.08)",
                                        border: isSelected
                                            ? "3px solid #8B5FBF"
                                            : "3px solid transparent",
                                    };
                            }
                        };

                        // Booking mode rendering - using dynamic card styling
                        return (
                            <div
                                key={gathering.id}
                                style={getCardStyling()}
                                onClick={() => !shouldDisable && handleGatheringClick(gathering)}
                                onMouseEnter={(e) => {
                                    if (!shouldDisable && cardState === "available") {
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                        e.currentTarget.style.boxShadow = isSelected
                                            ? "0 12px 30px rgba(139, 95, 191, 0.4)"
                                            : "0 8px 25px rgba(139, 95, 191, 0.2)";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!shouldDisable && cardState === "available") {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = isSelected
                                            ? "0 8px 25px rgba(139, 95, 191, 0.3)"
                                            : "0 4px 15px rgba(139, 95, 191, 0.08)";
                                    }
                                }}
                            >
                                {/* Checkbox overlay - hide for certain states */}
                                {cardState !== "going" && cardState !== "temp-conflict" && (
                                    <div style={{
                                        position: "absolute",
                                        top: "12px",
                                        left: "12px",
                                        zIndex: 10,
                                        backgroundColor: "#fff",
                                        borderRadius: "50%",
                                        padding: "8px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "36px", // Fixed size to ensure perfect centering
                                        height: "36px", // Fixed size to ensure perfect centering
                                        boxSizing: "border-box",
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => !shouldDisable && handleGatheringClick(gathering)}
                                            disabled={shouldDisable}
                                            style={{
                                                width: "20px",
                                                height: "20px",
                                                cursor: shouldDisable ? "not-allowed" : "pointer",
                                                accentColor: "#4A4A4A",
                                                margin: 0,
                                                padding: 0,
                                                border: "none",
                                                outline: "none",
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                )}

                                {/* Going indicator for already booked gatherings */}
                                {cardState === "going" && (
                                    <div style={{
                                        position: "absolute",
                                        top: "12px",
                                        left: "12px",
                                        backgroundColor: "#22C55E",
                                        color: "#fff",
                                        borderRadius: "12px",
                                        padding: "6px 12px",
                                        fontSize: "0.8rem",
                                        fontWeight: "600",
                                        zIndex: 10,
                                    }}>
                                        ✓ Going
                                    </div>
                                )}

                                {/* Selected indicator */}
                                {isSelected && cardState === "available" && (
                                    <div style={{
                                        position: "absolute",
                                        top: "12px",
                                        right: "12px",
                                        backgroundColor: "#8B5FBF",
                                        color: "#fff",
                                        borderRadius: "12px",
                                        padding: "4px 8px",
                                        fontSize: "0.75rem",
                                        fontWeight: "600",
                                        zIndex: 10,
                                    }}>
                                        ✓ Selected
                                    </div>
                                )}

                                {/* Date conflict warning message */}
                                {dateConflictMessage === gathering.id && (
                                    <div style={{
                                        position: "absolute",
                                        bottom: "12px",
                                        left: "12px",
                                        right: "12px",
                                        backgroundColor: "#FEF2F2",
                                        border: "2px solid #F87171",
                                        borderRadius: "8px",
                                        padding: "8px 12px",
                                        zIndex: 15,
                                        boxShadow: "0 4px 12px rgba(248, 113, 113, 0.3)",
                                        animation: "fadeInScale 0.3s ease-out",
                                    }}>
                                        <p style={{
                                            margin: 0,
                                            fontSize: "0.8rem",
                                            color: "#DC2626",
                                            fontWeight: "600",
                                            textAlign: "center",
                                            lineHeight: "1.3",
                                        }}>
                                            ⚠️ You've already selected a gathering on this date
                                        </p>
                                    </div>
                                )}

                                {/* Image */}
                                <div style={{
                                    height: "200px",
                                    background: `linear-gradient(45deg, ${displayGatheringType === 'professional' ? '#8B5FBF' : '#2c2e96'}, ${displayGatheringType === 'professional' ? '#6B46C1' : '#8B5FBF'})`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontSize: "3rem",
                                    position: "relative",
                                    overflow: "hidden",
                                }}>
                                    {getGatheringImage(displayGatheringType, displayThemeId, themes) ? (
                                        <img
                                            src={getGatheringImage(displayGatheringType, displayThemeId, themes)}
                                            alt={`${displayGatheringType} gathering`}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                            onError={(e) => {
                                                // Fallback to gradient background with emoji if image fails
                                                e.target.style.display = "none";
                                                e.target.parentElement.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; font-size: 3rem;">${displayGatheringType === 'professional' ? '🤝' : '🍻'}</div>`;
                                            }}
                                        />
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '3rem' }}>
                                            {displayGatheringType === 'professional' ? '🤝' : '🍻'}
                                        </div>
                                    )}
                                    {!isSelected && (
                                        <div style={{
                                            position: "absolute",
                                            top: "10px",
                                            right: "10px",
                                            backgroundColor: displayGatheringType === 'professional' ? "rgba(139, 95, 191, 0.9)" : "rgba(44, 46, 150, 0.9)",
                                            color: "#fff",
                                            padding: "4px 8px",
                                            borderRadius: "12px",
                                            fontSize: "0.8rem",
                                            fontWeight: "600",
                                        }}>
                                            {displayLabel}
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div style={{ padding: "20px" }}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        marginBottom: "12px",
                                    }}>
                                        <h4 style={{
                                            margin: 0,
                                            fontSize: "1.1rem",
                                            fontWeight: "700",
                                            color: shouldDisable ? "#888" : "#333",
                                        }}>
                                            {themeName ? `${themeName}` : `${displayLabel}`}
                                        </h4>
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "6px 10px",
                                            backgroundColor: cardState === "going"
                                                ? "#DCFCE7" // Light green for going state
                                                : isSelected
                                                    ? "rgba(139, 95, 191, 0.2)"
                                                    : shouldDisable
                                                        ? "rgba(200, 200, 200, 0.3)"
                                                        : "rgba(139, 95, 191, 0.1)",
                                            borderRadius: "12px",
                                            fontSize: "0.75rem",
                                            fontWeight: "600",
                                            color: cardState === "going"
                                                ? "#16A34A" // Dark green text for going state
                                                : isSelected
                                                    ? "#8B5FBF"
                                                    : shouldDisable
                                                        ? "#888"
                                                        : "#8B5FBF",
                                        }}>
                                            {cardState === "going"
                                                ? "Signed Up"
                                                : isSelected
                                                    ? displayLabel
                                                    : shouldDisable
                                                        ? (cardState === "conflict" ? "Already Signed Up" : "Unavailable")
                                                        : "Click to Select"
                                            }
                                        </div>
                                    </div>

                                    <p style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "0.95rem",
                                        color: shouldDisable ? "#888" : "#666",
                                        fontWeight: "600",
                                    }}>
                                        📅 {readableDate}
                                    </p>

                                    {(userLocation?.city) && (
                                        <p style={{
                                            margin: "0 0 8px 0",
                                            fontSize: "0.9rem",
                                            color: shouldDisable ? "#aaa" : "#666",
                                        }}>
                                            📍 {userLocation.city}{userLocation.area ? ` — ${userLocation.area}` : ""}
                                        </p>
                                    )}

                                    {gathering.venue_name && (
                                        <p style={{
                                            margin: "0 0 8px 0",
                                            fontSize: "0.9rem",
                                            color: shouldDisable ? "#aaa" : "#555",
                                        }}>
                                            🏛 {gathering.venue_name}{gathering.venue_address ? `, ${gathering.venue_address}` : ""}
                                        </p>
                                    )}

                                    {/* Status message */}
                                    {statusMessage && (
                                        cardState === "going" ? (
                                            <div style={{
                                                marginTop: "10px",
                                                padding: "10px 14px",
                                                backgroundColor: "#DCFCE7",
                                                border: "1.5px solid #22C55E",
                                                borderRadius: "8px",
                                                fontSize: "0.9rem",
                                                fontWeight: "700",
                                                color: "#15803D",
                                                lineHeight: "1.4",
                                            }}>
                                                {statusMessage}
                                            </div>
                                        ) : (
                                            <p style={{
                                                margin: 0,
                                                fontSize: "0.8rem",
                                                color: cardState === "temp-conflict"
                                                    ? "#DC2626"
                                                    : shouldDisable
                                                        ? "#888"
                                                        : "#666",
                                                fontStyle: "italic",
                                                lineHeight: "1.3",
                                                fontWeight: cardState === "temp-conflict" ? "600" : "normal",
                                            }}>
                                                {statusMessage}
                                            </p>
                                        )
                                    )}
                                </div>
                            </div>
                        );
                    }
                })}
            </div>

            {/* No results message */}
            {filteredGatherings.length === 0 && (
                <div style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    backgroundColor: "#fff",
                    borderRadius: "15px",
                    boxShadow: "0 4px 15px rgba(139, 95, 191, 0.08)",
                }}>
                    <p style={{
                        fontSize: "1.1rem",
                        color: "#666",
                        margin: 0,
                    }}>
                        {gatheringType === 'professional' && selectedTheme && allSlots.length > 0
                            ? `No upcoming slots available for the selected theme. ${getThemeSpecificMessage()}`
                            : "No gatherings match your current filters. Try adjusting your selection above."
                        }
                    </p>
                </div>
            )}
        </div>
    );
};

export default GatheringsList;